import { requestAccessToken } from '../../api/auth.api'
import { isSuiteSsoRedirectMode } from '../sso-flags'
import { resolvePayAccessTokenForMicroservices, resolvePayUserSessionFromRefreshToken } from './pay-auth-bridge.service'

function getStoredPayRefreshToken(): string {
  const raw = globalThis.sessionStorage.getItem('token') || globalThis.localStorage.getItem('token')
  return raw?.replaceAll('"', '') ?? ''
}

/**
 * Bearer token for app-data / app-queries and dashboard calls.
 * SSO mode → bridge Identity session to Pay RS256 access token via /auth/request-access-token.
 */
export async function resolveAppAccessToken(
  activateAuth?: (token: { token: string }) => void
): Promise<string> {
  if (isSuiteSsoRedirectMode()) {
    return resolvePayAccessTokenForMicroservices(activateAuth)
  }

  const payRefreshToken = getStoredPayRefreshToken()

  if (!payRefreshToken) {
    throw new Error('Pay refresh token is required outside suite SSO mode')
  }

  const {
    data: { accessToken }
  } = await requestAccessToken(payRefreshToken)

  return accessToken
}

/**
 * Whether dashboard data loaders can run (SSO bootstrap done or legacy Pay session).
 */
export function isPayMongoUserId(value: string | undefined): boolean {
  return Boolean(value && /^[a-f\d]{24}$/i.test(value))
}

/** Pay microservices use the Mongo system-user id from JWT `user_data`, not the suite profile uuid. */
export function resolvePaySystemUserId(userSessionId: string | undefined): string | undefined {
  if (isPayMongoUserId(userSessionId)) {
    return userSessionId
  }

  const fromRefreshToken = resolvePayUserSessionFromRefreshToken()?._id

  if (isPayMongoUserId(fromRefreshToken)) {
    return String(fromRefreshToken)
  }

  return undefined
}

export function canLoadDashboardAppData(userSessionId: string | undefined, legacyIsAuth: string | undefined): boolean {
  if (!resolvePaySystemUserId(userSessionId)) {
    return false
  }

  if (isSuiteSsoRedirectMode()) {
    return globalThis.sessionStorage.getItem('suiteDashboardReady') === 'true'
  }

  return Boolean(legacyIsAuth?.replaceAll('"', '').trim())
}
